import { deepProp } from "@/helpers/functions/ObjectHelpers";

/**
 * @typedef {Object.<string, *>} ObjectType
 */

/**
 * Checks if the provided value is an array.
 *
 * @template T
 * @param {*} array
 * @returns {boolean}
 */
export function isSafeArray(array) {
  return Array.isArray(array);
}

/**
 * Converts the provided value into an array when possible.
 *
 * @template T
 * @param {*} array
 * @returns {T[]}
 */
export function safeArray(array) {
  if (!isSafeArray(array)) return [];

  return /** @type {T[]} */ (array);
}

/**
 * Returns the number of elements in the provided array.
 *
 * @template T
 * @param {Array<T> | *} array
 * @returns {number}
 */
export function size(array) {
  return safeArray(array).length;
}

/**
 * Extracts items from an array.
 *
 * @template T
 * @param {Array<T> | *} arr
 * @param {string} key
 * @returns {Array<*>}
 */
export function PluckItem(arr, key) {
  return safeArray(arr).map((item) => item[key]);
}

/**
 * Picks multiple keys from each item.
 *
 * @template T
 * @param {Array<T> | *} arr
 * @param {Array<string>} keys
 * @returns {Array<ObjectType>}
 */
export function PluckItems(arr, keys) {
  return safeArray(arr).map((item) =>
    keys.reduce((acc, key) => {
      const value = deepProp(item, key);
      acc[key] = value == null ? acc[key] : value;
      return acc;
    }, /** @type {ObjectType} */ ({}))
  );
}

/**
 * Groups array elements by key.
 *
 * @template T
 * @param {Array<T> | *} arr
 * @param {string} key
 * @returns {ObjectType}
 */
export function groupBy(arr, key) {
  return safeArray(arr).reduce((acc, current) => {
    const groupKey = key.includes(".") ? deepProp(current, key) : current[key];

    if (!acc[groupKey]) acc[groupKey] = [];

    acc[groupKey].push(current);
    return acc;
  }, /** @type {ObjectType} */ ({}));
}

/**
 * Produces a duplicate-free version of the array, using === to test equality.
 *
 * @template T
 * @param {Array<T> | *} array
 * @returns {T[]}
 */
export function uniq(array) {
  return [...new Map(safeArray(array).map((item) => [item, item])).values()];
}

/**
 * Produces a duplicate-free version based on a custom comparator.
 *
 * @template T
 * @param {Array<T> | *} array
 * @param {(element: T, step: T) => number} callbackFn
 * @returns {T[]}
 */
export function uniqWith(array, callbackFn) {
  const source = safeArray(array);

  return source.filter((element, index) => {
    return source.findIndex((step) => callbackFn(element, step)) === index;
  });
}

/**
 * Reduces an array of objects into an object keyed by `key`.
 *
 * @template T
 * @param {Array<T> | *} arr
 * @param {string} key
 * @returns {ObjectType}
 */
export function reduceBy(arr, key) {
  return safeArray(arr).reduce((acc, current) => {
    const groupKey = key.includes(".") ? deepProp(current, key) : current[key];
    acc[groupKey] = current;
    return acc;
  }, /** @type {ObjectType} */ ({}));
}

/**
 * Creates an array containing a contiguous sequence of numbers.
 *
 * @param {number} size
 * @param {number} [start=0]
 * @returns {number[]}
 */
export function range(size, start = 0) {
  return Array.from({ length: size }).map((_, num) => num + start);
}
